import React from 'react';
import type Variant from '@modules/product/type/variant';
import { useLatest } from 'ahooks';
import { useUser } from '~/modules/user';
import getLowestPriceInStockVariant from '../util/get-lowest-price-in-stock-variant';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useLandingVariant } from '../hook/use-landing-variant';

interface VariantSelectionProviderProps {
    product: ProductPageLoaderData['product'];
    children: React.ReactNode | React.ReactNode[];
}

interface VariantSelectionState {
    selectedVariant: Variant | null;
    setSelectedVariant(v: Variant | null): void;
    quantity: number;
    setQuantity(qty: number): void;
}

const VariantSelectionContext = React.createContext<
    VariantSelectionState | undefined
>(undefined);

const useVariantSelectionState = (): VariantSelectionState => {
    const context = React.useContext(VariantSelectionContext);
    if (!context) {
        throw new Error('Cannot be used outside VariantSelectionContext');
    }
    return context;
};

const VariantSelectionProvider = ({
    product,
    children,
}: VariantSelectionProviderProps) => {
    const productRef = useLatest(product);
    const user = useUser();
    const isLoggedIn = user.__typename === 'Me';

    const landingVariant = useLandingVariant(product);
    const landingVariantID = landingVariant?.ID;

    const [selectedVariant, setSelectedVariant] =
        React.useState<Variant | null>(
            (product.Variants.length
                ? landingVariant ?? product.Variants[0]
                : product.MasterVariant) ?? null
        );

    const selectedVariantRef = useLatest(selectedVariant);

    const [quantity, setQuantity] = React.useState(1);

    const value = React.useMemo(() => {
        return {
            selectedVariant,
            setSelectedVariant,
            quantity,
            setQuantity,
        };
    }, [selectedVariant, quantity]);

    React.useEffect(() => {
        const productRefValue = productRef.current;
        if (!productRefValue) {
            setSelectedVariant(null);
            return;
        }

        if (
            !productRefValue.MasterVariant &&
            !productRefValue.Variants.length
        ) {
            setSelectedVariant(null);
            return;
        }
        if (
            productRefValue.MasterVariant &&
            productRefValue.Variants.length === 0
        ) {
            setSelectedVariant(productRefValue.MasterVariant);
        } else if (productRefValue.Variants.length) {
            const landingVariant = landingVariantID
                ? productRefValue.Variants.find(
                      (v) => v.ID === landingVariantID
                  )
                : null;
            const lowestVariantID =
                productRefValue.PricingDisplay &&
                'ReferenceVariant' in productRefValue.PricingDisplay
                    ? productRefValue.PricingDisplay.ReferenceVariant?.ID
                    : getLowestPriceInStockVariant(productRefValue.Variants);
            const lowestPriceVariant = lowestVariantID
                ? productRefValue.Variants.find((v) => v.ID === lowestVariantID)
                : productRefValue.Variants[0];
            setSelectedVariant(
                landingVariant ??
                    lowestPriceVariant ??
                    productRefValue.Variants[0] ??
                    null
            );
        } else {
            setSelectedVariant(null);
        }
        return () => {
            setQuantity(1);
        };
    }, [product.ID, productRef, landingVariantID, isLoggedIn, setQuantity]);

    React.useEffect(() => {
        const currentVariant = selectedVariantRef.current;
        if (!currentVariant) return;

        let foundVariant =
            product.MasterVariant?.ID === currentVariant.ID
                ? product.MasterVariant
                : product.Variants.find((v) => v.ID === currentVariant?.ID);

        if (
            foundVariant &&
            JSON.stringify(foundVariant) !== JSON.stringify(currentVariant)
        ) {
            setSelectedVariant(foundVariant);
        }
    }, [product, selectedVariantRef]);

    return (
        <VariantSelectionContext.Provider value={value}>
            {children}
        </VariantSelectionContext.Provider>
    );
};

export { VariantSelectionProvider, useVariantSelectionState };
